import AccordianBanner from 'components/UI/AccordianBanner';
import RichText from 'components/UI/Richtext';
import Link from 'components/UI/Link';
import { ReactElement, useState } from 'react';
import { IPageLocationFields } from 'types/contentful';
import { Document } from '@contentful/rich-text-types';

type EnrichedLocation = string | { slug: string; locationData: IPageLocationFields | null };

interface LocationHubsAccordionProps {
  id: string;
  heading: Document;
  copy?: Document | undefined;
  copyAlignment?: 'left' | 'center' | 'right';
  locationHubsGroupings?: Array<{
    fields: {
      id: string;
      heading: Document;
      copy?: Document | undefined;
      locations: EnrichedLocation[];
    };
  }>;
}

export default function LocationHubsAccordion({
  id,
  heading,
  copy,
  copyAlignment = 'center',
  locationHubsGroupings,
}: LocationHubsAccordionProps): ReactElement {
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set());

  const toggleAccordion = (index: number): void => {
    setOpenIndices((prev) => {
      const updated = new Set(prev);
      if (updated.has(index)) {
        updated.delete(index);
      } else {
        updated.add(index);
      }
      return updated;
    });
  };

  if (!locationHubsGroupings || locationHubsGroupings.length === 0) return <></>;

  console.log(copyAlignment);

  return (
    <div id={id} className="ecp-my py-10 md:py-20 px-16 px-site-component mx-auto bg-neutral-alt">
      <div className="flex flex-col lg:flex-row justify-center gap-8 lg:gap-6">
        {heading && (
          <div className="w-full lg:w-1/2">
            <div className="bordered-heading-richtext text-left text-primary-actual md:mb-8 font-light">
              <RichText
                document={heading}
                classNames="text-new-34 lg:text-4xl lg:leading-new-43 max-w-none richtext-nomargin-headings richtext-font-inherit"
                noEmpty={true}
              />
            </div>
          </div>
        )}
        {copy && (
          <div className="w-full lg:w-1/2">
            <RichText document={copy} classNames={`text-lg text-primary-actual text-left`} />
          </div>
        )}
      </div>
      {(heading || copy) && <div className="block mb-8 lg:mb-16"></div>}
      {locationHubsGroupings.map((hub, i) => {
        const hubFields = hub.fields;

        return (
          <div key={i}>
            <AccordianBanner
              className="bg-white p-4 md:px-8 md:py-6 accordion-location-hub-heading"
              type="FAQ"
              onClick={() => toggleAccordion(i)}
              isOpen={openIndices.has(i)}
              header={
                <RichText
                  document={hubFields.heading}
                  classNames="max-w-none font-light"
                  noEmpty={true}
                />
              }
            />
            {openIndices.has(i) && (
              <div className="p-4 md:pt-6 md:pb-8 md:px-8 bg-white border-t border-gray-300">
                {hubFields.copy && (
                  <div className="mb-4">
                    <RichText document={hubFields.copy} classNames="text-lg text-primary-actual" />
                  </div>
                )}
                {hubFields.locations && hubFields.locations.length > 0 && (
                  <ul className="list-none grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-y-4 gap-x-6">
                    {hubFields.locations.map((location, locationIndex) => {
                      // Handle both old format (string) and new format (object with slug and locationData)
                      const locationSlug = typeof location === 'string' ? location : location.slug;
                      const locationData =
                        typeof location === 'string' ? null : location.locationData;
                      const displayName = locationData?.name ?? locationSlug;

                      return (
                        <li key={locationIndex} className="h-full">
                          <Link slug={`locations/${locationSlug}`}>
                            <a className="text-lg teal-fontcolor hover:text-primary-actual bg-neutral-alt hover:bg-neutral-alt/30 p-2.5 flex items-center justify-start gap-3 h-full">
                              <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                >
                                  <g clipPath="url(#clip0_3547_2710)">
                                    <path
                                      d="M17.5 8.33301C17.5 14.1663 10 19.1663 10 19.1663C10 19.1663 2.5 14.1663 2.5 8.33301C2.5 6.34388 3.29018 4.43623 4.6967 3.02971C6.10322 1.62318 8.01088 0.833008 10 0.833008C11.9891 0.833008 13.8968 1.62318 15.3033 3.02971C16.7098 4.43623 17.5 6.34388 17.5 8.33301Z"
                                      stroke="white"
                                      strokeWidth="1.66667"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M10 10.833C11.3807 10.833 12.5 9.71372 12.5 8.33301C12.5 6.9523 11.3807 5.83301 10 5.83301C8.61929 5.83301 7.5 6.9523 7.5 8.33301C7.5 9.71372 8.61929 10.833 10 10.833Z"
                                      stroke="white"
                                      strokeWidth="1.66667"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </g>
                                  <defs>
                                    <clipPath id="clip0_3547_2710">
                                      <rect width="20" height="20" fill="white" />
                                    </clipPath>
                                  </defs>
                                </svg>
                              </div>
                              <span className="flex-grow">{displayName}</span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="11"
                                height="19"
                                viewBox="0 0 11 19"
                                fill="none"
                              >
                                <path
                                  d="M1.33301 1.33301L9.33301 9.33301L1.33301 17.333"
                                  stroke="#00C1D4"
                                  strokeWidth="2.66667"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </a>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            <div className="block mb-4"></div>
          </div>
        );
      })}
    </div>
  );
}
